<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Offcanvas, Row } from '@sveltestrap/sveltestrap';
  import { differenceInDays, format as formatDate } from 'date-fns';
  import { unparse } from 'papaparse';

  import { AttachmentsChartTimeSeries } from '@client/components/charts/attachments';
  import { EngagementChart } from '@client/components/charts/engagement';
  import { FilterBar } from '@client/components/filter-bar';
  import { DateFilter } from '@client/components/filters/date';
  import { MultiSelectFilter } from '@client/components/filters/multiselect';
  import { SearchFilter } from '@client/components/filters/search';
  import { SortFilter } from '@client/components/filters/sort';
  import { Icon } from '@client/components/icon';
  import { KPI } from '@client/components/kpi';
  import { Loader } from '@client/components/loader';
  import { GoogleMap } from '@client/components/map';
  import { MessageCard } from '@client/components/message-card';
  import { getNotificationsContext } from '@client/components/notifications';
  import { ReloadNotice } from '@client/components/reload-notice';
  import { Table } from '@client/components/table';

  import { MessagesService } from '@client/services/messages';

  import { appStore } from '@client/stores/app';
  import { activeNotifications } from '@client/stores/notifications';
  import { activitySettingsStore } from '@client/stores/page-settings';

  import { MessageLogParser } from '@client/utils/activity';
  import { downloadFile } from '@client/utils/download';
  import { QueryParams } from '@client/utils/query-params';

  import {
    ACTIVITY_EVENTS,
    ACTIVITY_FILTERS,
    ACTIVITY_MESSAGE_FILTERS,
    ACTIVITY_METRICS,
    ACTIVITY_STATS,
    LEAD_SCORES
  } from '@client/enums';

  import { sortCollection } from '@common/utils/arrays';
  import { getDateFromToday, resolveDateToken } from '@common/utils/dates';
  import { debounce } from '@common/utils/debounce';
  import { merge } from '@common/utils/objects';

  import { ActivityCell, EmailCell, IntentCell, SentCell, StatusCell } from './cell-formatters';
  import { IntentScoreHeader } from './header-formatters';
  import { formatResponse } from './response';
  import { RowExpansion } from './row-expansion';

  const { basepath, context, showActivityOnly, uiSettings = {} } = getContext('config');
  const { getNotifications } = getNotificationsContext();

  let DEFAULT_START_DATE = getDateFromToday(59);
  let DEFAULT_END_DATE = new Date();
  let DEFAULT_DAYS = 60;
  let DEFAULT_DAYS_SFDC = 365;

  // this had to be redefined here for salesforce widgets.
  // TODO: test this change everywhere and move to 800 vs 600
  let mobile = window.innerWidth < 800;
  let loading = false;
  let activeDashboard = 'engagement';
  let hideOverflow = false;
  let isSalesforce = context?.source === 'sfdc';
  let showFilters =
    window.innerWidth <= 800 && isSalesforce
      ? false
      : $appStore.mobile && $activitySettingsStore.showFilters !== undefined
        ? $activitySettingsStore.showFilters
        : true;
  let sortKey = mobile ? 'lastactivity.utcts' : 'lastactivity';
  let sortDirection = -1;
  let originalLeadScores = { ...LEAD_SCORES };
  let filters = isSalesforce
    ? merge({}, ACTIVITY_FILTERS)
    : merge({}, ACTIVITY_FILTERS, $activitySettingsStore.filters);
  let firstRun = $activitySettingsStore.firstRun === 0;

  if (!isSalesforce) {
    filters.search = QueryParams.get('search') || filters.search;
    filters.tags = QueryParams.get('tags') || filters.tags;
    filters.attributes = QueryParams.get('attributes') || filters.attributes;
  }

  filters.scores = filters.scores || { ...originalLeadScores };

  let hidePerformanceMetricsState = $activitySettingsStore.hidePerformanceMetrics;
  let hideChartsState = isSalesforce || ($activitySettingsStore.hideCharts ?? true);
  let showActivityOnlyState = $activitySettingsStore.showActivityOnly;

  let showActivityOnlyFlag = showActivityOnly || uiSettings?.showActivityOnly || showActivityOnlyState || false;
  let hidePerformanceMetrics =
    showActivityOnlyFlag && firstRun ? uiSettings?.showActivityMetrics !== true : hidePerformanceMetricsState;
  let hideCharts = showActivityOnlyFlag && firstRun ? uiSettings?.showActivityCharts !== true : hideChartsState;

  if (showActivityOnlyFlag && firstRun) {
    $activitySettingsStore.hidePerformanceMetrics = hidePerformanceMetrics;
    $activitySettingsStore.hideCharts = hideCharts;
    $activitySettingsStore.firstRun = 1;
  }

  let startDate = resolveDateToken(QueryParams.get('startDate'));
  let endDate = resolveDateToken(QueryParams.get('endDate'));

  let attributeFilters = Object.keys(ACTIVITY_MESSAGE_FILTERS).reduce((acc, key) => {
    acc[key] = ACTIVITY_MESSAGE_FILTERS[key];

    acc[key].enabled = filters.attributes.split(',').includes(ACTIVITY_MESSAGE_FILTERS[key].value);
    return acc;
  }, {});

  if (!isSalesforce) {
    $activitySettingsStore.filters = { ...filters };
  }

  let leadscores = filters.scores;
  let days =
    (startDate && endDate && Math.max(1, differenceInDays(new Date(), new Date(startDate)) + 1)) ||
    (isSalesforce ? DEFAULT_DAYS_SFDC : DEFAULT_DAYS);

  let tagFilters = {};
  let sortFilters = {};

  let kpis = { ...ACTIVITY_STATS };
  let kpisChangeSinceLastPeriod = { ...kpis };

  let engagementChartData = {};

  let columns = [];
  let expanded = [];
  let rows = [];

  let mapFilters = Object.keys(ACTIVITY_EVENTS);
  let markers = [];

  let align = 'right';

  let originalResponse = [];
  let initial = 0;

  const historyCache = {};

  let showEntireHistory = {};

  const EXPORT_DATE_FORMAT = 'M-dd-yyyy-hh-mm-ss';
  const EXPORT_TYPE = 'text/csv;charset=utf-8';

  const activityEvents = Object.entries(ACTIVITY_EVENTS).sort((a, b) => a[1].label.localeCompare(b[1].label));

  const getEntireHistory = async ({ detail }) => {
    showEntireHistory[detail.msglink] = true;

    loading = true;

    if (historyCache[detail.msglink] === undefined) {
      historyCache[detail.msglink] = await MessagesService.getMessageData(detail.msglink);
    }

    loading = false;

    // Full refresh needed to incorporate the newly fetched complete message history
    await update();
  };

  const createTagFilters = (data) => {
    const definitions = data.reduce((acc, { tags }) => {
      const items = tags?.split(', ') ?? [];

      if (items.length) {
        items.forEach((item) => {
          if (!acc[item]) {
            acc[item] = {
              label: item,
              value: item,
              enabled: filters.tags.includes(item)
            };
          }
        });
      }

      return acc;
    }, {});

    return Object.keys(definitions)
      .sort()
      .reduce((acc, key) => (acc[key] = definitions[key]) && acc, {});
  };

  const update = async (skip) => {
    loading = true;

    let response = originalResponse;

    if (!skip || (skip?.type !== 'filter' && skip?.type !== 'reprocess')) {
      // Only fetch extra data for previous period if performance metrics are enabled
      const totalDays = hidePerformanceMetrics ? days : days * 2;

      response = (await MessagesService.get({ days: totalDays })) || [];

      let results = [];

      for (let i = 0; i < response.length; i++) {
        const item = response[i];
        let activity = [];

        if (showEntireHistory[item.msglink] && historyCache[item.msglink]) {
          activity = historyCache[item.msglink];
        }

        results = [...activity, ...results];
      }

      response = Array.isArray(response) ? [...response, ...results] : results;
      originalResponse = response;
    }

    response = formatResponse(
      response,
      filters.search,
      attributeFilters,
      tagFilters,
      startDate || DEFAULT_START_DATE,
      endDate || DEFAULT_END_DATE,
      leadscores,
      historyCache
    );

    rows = response.queryResult;
    kpis = response.queryStats;
    engagementChartData = response.engagementChartData;
    kpisChangeSinceLastPeriod = response.change;
    markers = response.markers;

    if (initial === 0) {
      initial++;
      tagFilters = createTagFilters(rows);
    }

    loading = false;

    if (mobile) {
      rows = sortCollection(rows, sortKey, sortDirection);

      if (selectedMessageData) {
        selectedMessageData = rows.find(({ msglink }) => msglink === selectedMessageData.msglink);
      }
    }

    if (!isSalesforce) {
      $activitySettingsStore.filters = {
        ...filters,
        tags: Object.keys(tagFilters)
          .map((key) => tagFilters[key].enabled && attributeFilters[key].value)
          .filter(Boolean)
          .join(),
        attributes: Object.keys(attributeFilters)
          .map((key) => attributeFilters[key].enabled && attributeFilters[key].value)
          .filter(Boolean)
          .join(),
        dateRange: {
          startDate,
          endDate
        }
      };
    }
  };

  const ENGAGEMENT_EVENT_REGEXP = /(downloaded|myplayerlaunched|viewerlaunched|myplayerlaunched|playerlaunched)/;

  const getColumns = () => [
    {
      key: 'lastactivity',
      title: $format('label.ACTIVITY'),
      tooltip: $format('label.TOOLTIP.ACTIVITY'),
      headerClass: 'last-activity-cell',
      value: ({ lastactivity }) => lastactivity.utcts,
      renderComponent: ActivityCell,
      sortable: true
    },
    {
      key: 'emailviewed',
      title: $format('label.STATUS'),
      headerClass: 'status-cell',
      value: ({ emailviewed }) => !!emailviewed,
      renderComponent: StatusCell,
      sortable: true
    },
    {
      key: 'msgsubject',
      title: $format('label.EMAIL'),
      headerClass: 'subject-cell',
      value: ({ msgsubject }) => msgsubject,
      renderComponent: {
        component: EmailCell,
        props: {
          searchText: filters.search
        }
      },
      sortable: true
    },
    {
      key: 'leadscore',
      title: {
        component: IntentScoreHeader,
        props: {
          leadscores,
          originalLeadScores,
          onUpdate(scores) {
            leadscores = merge(leadscores, scores);
            if (!isSalesforce) $activitySettingsStore.filters.scores = leadscores;
            update(true);
          },
          onReset() {
            leadscores = merge({}, originalLeadScores);
            if (!isSalesforce) $activitySettingsStore.filters.scores = leadscores;
            update(true);
          }
        }
      },
      columnClass: 'align-right',
      tooltip: $format('label.TOOLTIP.INTENT_SCORE'),
      value: ({ leadscore }) => leadscore,
      renderComponent: IntentCell,
      sortable: true
    },
    {
      key: 'engagement',
      title: $format('label.ENGAGEMENT'),
      tooltip: $format('label.TOOLTIP.ENGAGEMENT'),
      columnClass: 'align-right',
      value: ({ activity }) => activity.filter(({ verb }) => ENGAGEMENT_EVENT_REGEXP.test(verb)).length,
      sortable: true
    },
    {
      key: 'clicks',
      title: $format('label.CLICKS'),
      columnClass: 'align-right',
      tooltip: $format('label.TOOLTIP.CLICKS_ACTIVITY'),
      value: ({ activity }) =>
        activity.filter(
          ({ objecttype }) => objecttype === 'linkclick' || objecttype === 'adclick' || objecttype === 'websitevisit'
        ).length,
      sortable: true
    },
    {
      key: 'views',
      title: $format('label.VIEWS'),
      tooltip: $format('label.TOOLTIP.VIEWS'),
      columnClass: 'align-right',
      value: ({ activity }) => activity.filter(({ objecttype }) => objecttype === 'beaconview').length,
      sortable: true
    },
    {
      key: 'msgtime',
      title: $format('label.SENT_ON'),
      columnClass: 'sent-cell',
      value: ({ msgtime }) => msgtime,
      renderComponent: SentCell,
      sortable: true
    }
  ];

  sortFilters = {
    lastactivity: {
      label: window.innerWidth < 400 ? $format('label.UPDATED') : $format('label.LAST_ACTIVITY'),
      value: 'lastactivity.utcts',
      enabled: true
    },
    msgtime: {
      label: $format('label.SENT'),
      value: 'msgtime',
      enabled: false
    },
    msgsubject: {
      label: $format('label.SUBJECT'),
      value: 'msgsubject',
      enabled: false
    }
  };

  const setActiveDashboard = (which) => {
    hideOverflow = true;

    activeDashboard = which;

    setTimeout(() => {
      hideOverflow = false;
    }, 50);
  };

  const toggleMapEvent = (type) => {
    if (mapFilters.includes(type)) {
      mapFilters.splice(mapFilters.indexOf(type), 1);
    } else {
      mapFilters.push(type);
    }

    mapFilters = [...mapFilters];
  };

  const onSearchFilterChange = debounce(() => {
    columns = getColumns();
    update({ type: 'filter' });
  }, 250);

  const onSortChange = (selected, key, dir) => {
    sortKey = key;
    sortDirection = dir === 'asc' ? -1 : 1;
    update(true);
  };

  columns = getColumns();

  const onAttributesFilterChange = () => update({ type: 'filter' });
  const onTagsFilterChange = () => update({ type: 'filter' });

  const onRowChange = ({ detail }) => {
    const findIndex = rows.find(({ msglink }) => msglink === detail.msglink);
    const findOriginal = originalResponse.find(({ msglink }) => msglink === detail.msglink);

    if (findOriginal) {
      findOriginal.tags = detail.tags;
      originalResponse = [...originalResponse];
    }

    if (findIndex) {
      findIndex.tags = detail.tags;
      rows = [...rows];
      tagFilters = createTagFilters(rows);
    }
  };

  const onResize = debounce(() => {
    mobile = window.innerWidth < 800;
  }, 150);

  let selectedMessageData = null;
  let showMessageDetails = false;

  const onMessageSelect = (e) => {
    selectedMessageData = null;
    selectedMessageData = e.detail;
    showMessageDetails = true;
  };

  const toggleMessageDetails = () => {
    showMessageDetails = !showMessageDetails;
  };

  const togglePerformanceMetrics = () => {
    const wasHidden = hidePerformanceMetrics;
    hidePerformanceMetrics = !hidePerformanceMetrics;
    $activitySettingsStore.hidePerformanceMetrics = hidePerformanceMetrics;

    // Only refetch if enabling performance metrics and we don't have enough historical data
    if (!hidePerformanceMetrics && wasHidden) {
      // Check if we need more data for previous period calculations
      const currentDataDays = Math.ceil(
        (new Date().getTime() -
          new Date(originalResponse[originalResponse.length - 1]?.utcts || new Date()).getTime()) /
          (1000 * 60 * 60 * 24)
      );
      const needsMoreData = currentDataDays < days * 2;

      if (needsMoreData) {
        update();
      } else {
        // Use existing data, just re-process it
        update({ type: 'reprocess' });
      }
    }
  };

  const toggleCharts = () => {
    hideCharts = !hideCharts;
    $activitySettingsStore.hideCharts = hideCharts;
  };

  const exportToCSV = () => {
    const fields = [
      $format('label.TO'),
      $format('label.SUBJECT'),
      $format('label.VIEWED'),
      $format('label.LINK_CLICKED'),
      $format('label.WEBSITE_VISITED'),
      $format('label.AD_CLICKED'),
      $format('label.VIDEO_LAUNCHED'),
      $format('label.DOCUMENT_VIEW'),
      $format('label.DOCUMENT_DOWNLOAD'),
      $format('label.INTENT_SCORE'),
      $format('label.TAGS'),
      $format('label.SENT_ON'),
      $format('label.LAST_ACTIVITY')
    ];

    const data = MessageLogParser.getExportArray(rows).map((row) => Object.values(row));

    const text = unparse({ fields, data });
    const blob = new Blob([text], { type: EXPORT_TYPE });
    const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

    downloadFile(blob, `activity-data-${today}.csv`);
  };

  onMount(async () => await update());

  $: includesTagFilters = Object.keys(tagFilters).length;
  $: align = includesTagFilters ? 'right' : mobile ? 'left' : 'right';
  $: $activitySettingsStore.showFilters = showFilters;
  $: cols = $appStore.mobile ? 12 : 9;
  $: showReloadNotice = $activeNotifications?.length && getNotifications()?.length;
  $: if (startDate && endDate) {
    days = Math.max(1, differenceInDays(new Date(), new Date(startDate)) + 1);
  }
</script>

<div id="activity-dashboard" class="page" class:standalone={showActivityOnlyFlag}>
  <Loader show={loading} backdrop="true" />
  {#if !showActivityOnlyFlag}
    <div class="page-header">
      <Container fluid>
        <Row>
          <Col md="12">
            <Breadcrumb>
              <BreadcrumbItem>
                <a href="{basepath}/insights" use:link>{$format('label.INSIGHTS')}</a>
              </BreadcrumbItem>
              <BreadcrumbItem active>{$format('label.ACTIVITY_DASHBOARD')}</BreadcrumbItem>
            </Breadcrumb>
          </Col>
        </Row>
        <Row class="flex-align--center">
          <Col sm={cols} md={cols} xs={cols}>
            <h1 class="page-title">
              {$format('label.ACTIVITY_DASHBOARD')}

              {#if !startDate && !endDate}
                <small
                  >{isSalesforce
                    ? $format('label.SHOWING_LAST_365_DAYS')
                    : $format('label.SHOWING_LAST_60_DAYS')}</small
                >
              {/if}
            </h1>
          </Col>
          {#if !$appStore.mobile}
            <Col sm="3" md="3" xs="3" class="text-right">
              <Button color="secondary" on:click={exportToCSV}>{$format('label.EXPORT')}</Button>
            </Col>
          {/if}
        </Row>
      </Container>
    </div>
  {/if}

  <div class="page-content">
    <div class="filters" class:active={hidePerformanceMetrics} class:no-filters={!showFilters}>
      <FilterBar bind:show={showFilters}>
        <SearchFilter bind:search={filters.search} onChange={onSearchFilterChange} />
        <MultiSelectFilter
          bind:filter={attributeFilters}
          onClick={onAttributesFilterChange}
          search={true}
          label={$format('label.EVENTS')}
          param="attributes"
        />

        {#if includesTagFilters}
          <MultiSelectFilter
            bind:filter={tagFilters}
            onClick={onTagsFilterChange}
            search={true}
            label={$format('label.TAGS')}
            param="tags"
          />
        {/if}

        <DateFilter
          isRange={true}
          shareTodayToken={true}
          bind:startDate
          bind:endDate
          bind:align
          showPresetsOnly={mobile}
          isMultipane={true}
          onDateChange={update}
          formatDate="MMM d, yyyy"
        />

        {#if mobile}
          <SortFilter bind:filter={sortFilters} onClick={onSortChange} sortDirParam="sortDir" sortKeyParam="sortKey" />
        {/if}

        <div
          class="feature-toggle"
          role="button"
          tabindex="0"
          class:active={!hidePerformanceMetrics}
          on:click={togglePerformanceMetrics}
        >
          <div class="dot">
            <i class:os-icon-circle={hidePerformanceMetrics} class:os-icon-circle-filled={!hidePerformanceMetrics} />
          </div>
          <span class="hide--tablet">{$format('label.PERFORMANCE_METRICS')}</span>
          <span class="hide--desktop">{$format('label.METRICS')}</span>
        </div>
        {#if context?.scope !== 'sdfc'}
          <div class="feature-toggle" role="button" tabindex="0" class:active={!hideCharts} on:click={toggleCharts}>
            <div class="dot">
              <i class:os-icon-circle={hideCharts} class:os-icon-circle-filled={!hideCharts} />
            </div>
            <span class="hide--tablet">{$format('label.ACTIVITY_CHARTS')}</span>
            <span class="hide--desktop">{$format('label.CHARTS')}</span>
          </div>
        {/if}
      </FilterBar>
    </div>
    <div
      class="dashboard-layout"
      class:no-filters={!showFilters}
      class:no-tags={!includesTagFilters}
      class:not-desktop={mobile}
      class:collapse-metrics={hidePerformanceMetrics}
      class:collapse-charts={hideCharts}
    >
      <div class="kpis" class:collapse-metrics={hidePerformanceMetrics}>
        {#each Object.entries(kpis) as [metric, value]}
          {#if ACTIVITY_METRICS[metric]}
            <KPI
              metric={$format(ACTIVITY_METRICS[metric].label)}
              value={ACTIVITY_METRICS[metric].formatter(value)}
              tooltip={$format(ACTIVITY_METRICS[metric].tooltip)}
              change={{
                type:
                  Number(kpisChangeSinceLastPeriod[metric]) === 0
                    ? ''
                    : kpisChangeSinceLastPeriod[metric] > 0
                      ? 'positive'
                      : 'negative',
                value: `${kpisChangeSinceLastPeriod[metric]}%`
              }}
            />
          {/if}
        {/each}
      </div>
      <small class="kpi-period" class:collapse-metrics={hidePerformanceMetrics}
        >{$format('label.PERCENT_CHANGE_BASED_ON_PREVIOUS_PERIOD')}</small
      >
      <div>
        <div class="charts">
          {#if !hideCharts}
            <div class="charts-layout">
              <div class="box" class:active={activeDashboard === 'maps'} class:overflow={hideOverflow}>
                {#if activeDashboard !== 'maps'}
                  <Button color="secondary" size="sm" on:click={() => setActiveDashboard('maps')}
                    >{$format('label.EXPAND')}</Button
                  >
                {/if}
                <div class="map">
                  <div class="legend">
                    {#each activityEvents as [metric, event]}
                      <div
                        class="item"
                        role="button"
                        tabindex="0"
                        class:disabled={!mapFilters.includes(metric)}
                        on:click={() => toggleMapEvent(metric)}
                      >
                        <div class="dot" style="background-color: {ACTIVITY_EVENTS[metric].color}" />
                        <div>{$format(ACTIVITY_EVENTS[metric].label)}</div>
                      </div>
                    {/each}
                    <div class="item proxy">
                      <div class="dot" />
                      <div>{$format('label.VPN_PROXIED_LOCATION')}</div>
                    </div>
                  </div>
                  <GoogleMap
                    height="530"
                    {mapFilters}
                    config={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      zoom: 4
                    }}
                    {markers}
                  />
                </div>
              </div>
              <div class="box" class:active={activeDashboard === 'engagement'} class:overflow={hideOverflow}>
                {#if activeDashboard !== 'engagement'}
                  <Button color="secondary" size="sm" on:click={() => setActiveDashboard('engagement')}
                    >{$format('label.EXPAND')}</Button
                  >
                {/if}
                <EngagementChart
                  title={$format('label.ENGAGEMENT_FREQUENCY')}
                  subtitle={$format('label.ENGAGEMENT_SUBTITLE')}
                  bind:data={engagementChartData}
                />
              </div>
              <div class="box" class:active={activeDashboard === 'attachments'} class:overflow={hideOverflow}>
                {#if activeDashboard !== 'attachments'}
                  <Button color="secondary" size="sm" on:click={() => setActiveDashboard('attachments')}
                    >{$format('label.EXPAND')}</Button
                  >
                {/if}
                <AttachmentsChartTimeSeries
                  title={$format('label.ATTACHMENT_ENGAGEMENT')}
                  subtitle={$format('label.ATTACHMENT_ENGAGEMENT_SUBTITLE')}
                  {startDate}
                  {endDate}
                  {rows}
                />
              </div>
            </div>
          {/if}
        </div>

        <main>
          {#if !mobile}
            <Table
              id="activity-feed"
              bind:rows
              bind:columns
              classNameRowExpanded="expanded-row"
              classNameExpandedContent="expanded-content"
              expandRowKey="msglink"
              emptyLabel={$format('label.MESSAGE.ACTIVITY_FEED_EMPTY')}
              expandOnRowClick={true}
              showExpandIcon={true}
              expandSingle={true}
              sortBy="lastactivity"
              sortOrder={-1}
              bind:expanded
            >
              <div slot="expanded" let:row>
                <RowExpansion
                  {row}
                  on:row-updated={onRowChange}
                  on:row-fetch-history={getEntireHistory}
                  bind:showEntireHistory
                  bind:tagFilters
                />
              </div>
            </Table>
          {:else}
            <div class="message-cards">
              {#if !rows.length}
                <div class="empty">{$format('label.MESSAGE.ACTIVITY_FEED_EMPTY')}</div>
              {:else}
                {#each rows as row}
                  <MessageCard
                    bind:data={row}
                    bind:sortKey
                    bind:searchText={filters.search}
                    on:message-details={onMessageSelect}
                  />
                {/each}
              {/if}
            </div>
            {#if showMessageDetails}
              <Offcanvas
                id="activity-offcanvas"
                isOpen={showMessageDetails}
                toggle={toggleMessageDetails}
                placement="end"
                style="width: 98%"
              >
                <Loader show={loading} backdrop="true" />
                <div slot="header" class="offcanvas-heading">
                  <Icon name="mail" />
                  <span>{selectedMessageData.msgsubject}</span>
                </div>
                <RowExpansion
                  bind:row={selectedMessageData}
                  on:row-updated={onRowChange}
                  on:row-fetch-history={getEntireHistory}
                  bind:showEntireHistory
                  bind:tagFilters
                />
              </Offcanvas>
            {/if}
          {/if}
        </main>
      </div>
    </div>
  </div>

  <ReloadNotice bind:show={showReloadNotice} />
</div>

<svelte:window on:resize={onResize} />

<style lang="scss" src="./activity.scss"></style>
